/**
 * @file api-customers-overview.ts
 * @description Сводка клиента для «Обзора» карточки (phase19.31, SC68):
 * `GET /admin/customers/:id/overview` — открытые заказы, что требует
 * внимания, задачи, заметки, связь, согласие на рекламу и «как покупает».
 *
 * Зеркало серверного `CustomerOverviewDto`
 * (apps/api customers/overview/customer-overview.service.ts; раздел «Сводка
 * клиента» в cabinet-api-contract.md на момент вёрстки ещё не записан). Ответ `{ data, error }` разбирает
 * `apiFetch`: ошибка приходит исключением `ApiError` с человеческим текстом.
 */

import { apiFetch } from "./api-client";
import type { ActivityKind } from "./api-crm";
import type { OrderStageKind } from "./api-orders";
import type { OrderChainOwner } from "./order-chain";

/** Открытый заказ клиента — блок «Сейчас в работе». */
export interface CustomerOverviewOrderDto {
  id: string;
  number: string;
  createdAt: string;
  totalGross: string;
  stageName: string;
  /** Вид стадии; `null` — у заказа нет стадии (старые заказы). */
  stageKind: OrderStageKind | null;
  stageColor: string | null;
  /** «Чей ход» — то же поле, что `ownerNow` в реестре заказов. */
  turn: OrderChainOwner | null;
  /** Доставка словами: «Пункт выдачи · СДЭК · в пути». */
  deliveryText: string;
  trackNumber: string | null;
}

export type CustomerAttentionKind = "UNPAID_ORDER" | "RETURN_IN_PROGRESS" | "OVERDUE_TASK" | "UNREAD_MESSAGE";

/** Строка «Требует внимания». Ссылки — по виду: заказ, задача или диалог. */
export interface CustomerAttentionDto {
  kind: CustomerAttentionKind;
  orderId?: string;
  orderNumber?: string;
  activityId?: string;
  conversationId?: string;
  title: string;
  /** С какого момента: дата заказа, срок задачи, время сообщения. */
  since: string;
}

/** Открытая задача клиента — блок «Задачи по клиенту». */
export interface CustomerOverviewTaskDto {
  id: string;
  kind: ActivityKind;
  title: string;
  dueAt: string | null;
  overdue: boolean;
  assigneeName: string | null;
}

/** Заметка о клиенте — блок «Заметки о клиенте». */
export interface CustomerOverviewNoteDto {
  id: string;
  title: string;
  content: string | null;
  authorName: string | null;
  createdAt: string;
}

/** Последний диалог с клиентом; `null` — переписки не было. */
export interface CustomerOverviewContactDto {
  conversationId: string;
  lastMessagePreview: string | null;
  lastMessageAt: string | null;
  lastMessageFromCustomer: boolean;
  unreadCount: number;
}

/** Строка «как получает / как платит»: подпись и сколько раз. */
export interface CustomerOverviewCountDto {
  label: string;
  count: number;
}

export interface CustomerOverviewBuyingDto {
  topProducts: { name: string; quantity: number; totalGross: string }[];
  receiveMethods: CustomerOverviewCountDto[];
  paymentMethods: CustomerOverviewCountDto[];
  /** Средний промежуток между заказами; `null` — заказов меньше двух. */
  avgDaysBetweenOrders: number | null;
  /** Дней с последнего заказа; `null` — заказов не было. */
  daysSinceLastOrder: number | null;
}

export interface CustomerOverviewDto {
  /**
   * Какие блоки доступны сотруднику: нет права — блок приходит пустым и не
   * показывается (заказы — права заказов, задачи и заметки — CRM, связь —
   * переписка с клиентами).
   */
  access: { orders: boolean; tasks: boolean; chat: boolean };
  openOrders: CustomerOverviewOrderDto[];
  openOrdersTotal: number;
  attention: CustomerAttentionDto[];
  tasks: CustomerOverviewTaskDto[];
  tasksTotal: number;
  notes: CustomerOverviewNoteDto[];
  notesTotal: number;
  contact: CustomerOverviewContactDto | null;
  marketingConsent: { granted: boolean; at: string | null };
  buying: CustomerOverviewBuyingDto;
}

export const customerOverview = {
  get: (customerId: string) => apiFetch<CustomerOverviewDto>(`/admin/customers/${customerId}/overview`),
};
